import ServicesSection from '../components/ServicesSection';
import CTASection from '../components/CTASection';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

const faqData = [
  {
    q: 'How often should I service my car?',
    a: 'Most manufacturers recommend a minor service every 10,000-15,000 km and a major service every 30,000-45,000 km. However, this can vary based on your car\'s make, model, and age. We can provide a specific schedule based on your vehicle.'
  },
  {
    q: 'Do you use genuine parts?',
    a: 'We prioritize using OEM (Original Equipment Manufacturer) and high-quality aftermarket parts from reputable suppliers to ensure the best performance and longevity for your vehicle. We provide clear options so you can make an informed choice.'
  },
  {
    q: 'Can you service a car that is still under warranty?',
    a: 'Yes, we can. We follow manufacturer-specified procedures and use approved parts, ensuring your warranty remains valid. We provide all necessary documentation for your records.'
  },
  {
    q: 'What are the signs my brakes need checking?',
    a: 'Common signs include a squealing or grinding noise, a pulsating or spongy brake pedal, the car pulling to one side when braking, or the brake warning light illuminating on your dashboard. If you notice any of these, get your brakes inspected immediately.'
  },
  {
    q: 'How long does a typical service take?',
    a: 'A minor service usually takes 2-3 hours, while a major service can take 4-6 hours. Repairs for specific issues will vary. We always provide an estimated timeframe when you book your service and keep you updated on the progress.'
  }
];

export default function Services() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const featureHighlights = [
    { title: t('whyChoose.expertise'), desc: t('whyChoose.expertise.desc') },
    { title: t('whyChoose.turnaround'), desc: t('whyChoose.turnaround.desc') },
    { title: t('whyChoose.transparent'), desc: t('whyChoose.transparent.desc') },
    { title: t('whyChoose.quality'), desc: t('whyChoose.quality.desc') },
  ];

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <SEO
        title="Car Services Ras Al Khor Dubai | Auto Repair & Maintenance - Vigor Auto"
        description="Expert car services in Ras Al Khor Dubai - engine repair, transmission, AC service, brakes, diagnostics, oil change & more. Dealer-alternative workshop for all brands. Serving Nad Al Hamar, Al Awir & International City. Free pickup/dropoff."
        keywords="car services Ras Al Khor, car repair Ras Al Khor, auto service centre Ras Al Khor, car engine repair dubai, car transmission repair dubai, car AC repair dubai, car brake service dubai, car diagnostics dubai, oil change dubai, car service Nad Al Hamar, auto repair Al Awir, european car service dubai, japanese car service dubai, korean car service dubai, dealer alternative dubai, car garage Ras Al Khor"
        schema={faqSchema}
      />
      <div className="bg-gray-50">
        {/* Hero */}
  <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative z-10">
              <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-4">Comprehensive Car Services in Ras Al Khor Dubai</h1>
              <p className="mt-4 max-w-2xl text-slate-200 text-sm md:text-base leading-relaxed">
                From routine maintenance to complex system diagnostics and repairs, our certified technicians at our Ras Al Khor car garage provide dealership-quality service with the care and transparency of an independent workshop. Serving Dubai's automotive community with expert care for European, Japanese, Korean, and American vehicles.
              </p>
            </div>
          </div>
        </section>

        {/* Service Grid */}
        <ServicesSection />

        {/* Features/Highlights */}
  <section className="py-10 md:py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{t('whyChoose.title')}</h2>
              <p className="mt-4 max-w-2xl mx-auto text-slate-600">
                {t('services.subtitle')}
              </p>
            </div>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {featureHighlights.map(f => (
                <div key={f.title} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200/80">
                  <h3 className="font-bold text-slate-800">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
  <section className="py-10 md:py-16 lg:py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-center text-2xl md:text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
            <p className="mt-4 text-center max-w-2xl mx-auto text-slate-600">
              Answers to common questions about our car services in Dubai.
            </p>
            <div className="mt-12 space-y-4">
              {faqData.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-sm border border-gray-200/80 overflow-hidden">
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex justify-between items-center text-left p-5 font-semibold text-slate-800"
                  >
                    <span>{faq.q}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-5 w-5 transform transition-transform ${openFaq === idx ? 'rotate-180' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {openFaq === idx && (
                    <div className="p-5 pt-0 text-slate-600 text-sm leading-relaxed">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTASection
          title="Ready for Expert Service?"
          subtitle="Schedule your visit today and experience the Vigor Auto difference. Honest, reliable, and professional car care is just a click away."
          buttonText="Request a Quote"
          buttonLink="/request-quote"
        />
      </div>
    </>
  );
}
